import { houseRoofOffset } from "./room/ember/two_storey_house"
import { Coordinate, TileSize } from "./models"
import { ItemTypeCharacteristics } from "./item/item_type_characteristics"
import type { TileMapMetaProvider } from "./tile_map_meta_provider"
import { TileInteractionTarget, tileInteractionsAt } from "./entity_tile_interactions"
import { autotile, wallAutoTileCellComputable } from "./room/autotiler"
import { isWallTopFloor } from "./room/ember/wall_top_floor"
// wall_top imports this module too: neither uses the other's exports until called.
import { wallClimbableAt, wallTopOpenAt } from "./room/ember/wall_top"

/**
 * Pointing a wall at an existing wall automatically targets the storey above it, raising that storey
 * when necessary. Empty cells use an existing floor or roof surface, then the player's own storey.
 * Shift explicitly selects stacking even over empty space. A roof over a raised wall roofs its storey.
 */

/** A cell to build on, and the storey it lands on when that is not the player's own. */
export type BuildTarget = Coordinate & { level?: number }

export type WallMap = Pick<TileMapMetaProvider, "getMapDeltaAt"> &
    Partial<Pick<TileMapMetaProvider, "resolveTileMapFromLayerId" | "isFloorOpeningAt">>

/** The player's storey or one linked above it. */
export interface StoreyView {
    level: number
    map: WallMap
}

/** A target with `level` only when there is one, so a plain cell stays exactly `{ x, y }`. */
export const withLevel = (point: Coordinate, level?: number): BuildTarget =>
    level === undefined ? { x: point.x, y: point.y } : { x: point.x, y: point.y, level }

/** The same cell on the same storey. (util's isEqual compares x and y only.) */
export const sameTarget = (a?: BuildTarget, b?: BuildTarget): boolean =>
    !!a && !!b && a.x === b.x && a.y === b.y && a.level === b.level

/** A built wall stands on this cell: its base (stamped with the cell as parent), or a column piece that blocks. */
export const wallCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sprite")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    const parent = delta.context?.parent
    if (parent && (parent.x !== at.x || parent.y !== at.y) && !delta.blocking) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "wall"
}

/** A built roof covers this cell. */
export const roofCellAt = (map: WallMap, at: Coordinate): boolean => {
    const delta = map.getMapDeltaAt?.(at.x, at.y, "sky")
    if (!delta || delta.removed || delta.context?.previewTarget) return false
    return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "roof"
}

/** The cell under `world` on a plane drawn `yOffset` from the player's (up is negative). */
export const cellOnPlane = (world: Coordinate, yOffset: number): Coordinate => ({
    x: Math.floor(world.x / TileSize),
    y: Math.floor((world.y - yOffset) / TileSize),
})

/**
 * The walls drawn where a pointer is `fy` of the way down cell `at` (0 its top edge, 1 its bottom), the likelier first:
 * the wall standing on the cell (a column piece too: the player laid it), or the wall whose cap it is. A wall's cell can
 * also carry the top of the wall south of it (the builder parents it to that wall: a corner's top rail runs along the
 * bottom of the cell above it), and there the lower half is that wall's top, the upper half the cell's own wall. Down a
 * column of walls the posts run together, and a wall is drawn up into the cell north of it, so the wall south of a
 * wall cell comes last: with the cell's own wall built on, pointing there raises the next wall down the column.
 */
export const wallsDrawnAt = (map: WallMap, at: Coordinate, fy = 0): Coordinate[] => {
    const parent = map.getMapDeltaAt?.(at.x, at.y, "sprite")?.context?.parent
    const owner = parent && wallCellAt(map, parent) ? { x: parent.x, y: parent.y } : undefined
    if (!wallCellAt(map, at)) return owner ? [owner] : []
    const own = { x: at.x, y: at.y }
    const south = { x: at.x, y: at.y + 1 }
    if (owner && owner.x === south.x && owner.y === south.y) return fy >= 0.5 ? [owner, own] : [own, owner]
    return wallCellAt(map, south) ? [own, south] : [own]
}

/** A wall laid atop a wall: where it goes, and the cell the pointer picked it by (on the plane of the storey it stands on). */
export interface StackPick {
    target: BuildTarget
    at: Coordinate
}

/** The cell under `world` on a plane drawn `yOffset` from the player's, and how far down it the point is (0 to 1). */
const pointOnPlane = (world: Coordinate, yOffset: number): { at: Coordinate; fy: number } => {
    const at = cellOnPlane(world, yOffset)
    return { at, fy: (world.y - yOffset) / TileSize - at.y }
}

/**
 * The storey above the wall under a pointer at `world`. Walls are picked where they are drawn, and the lowest
 * with nothing on it yet wins: a raised wall is drawn over the walls behind it on the storey below, which still take the
 * next wall first, so a house goes up a storey at a time. When every wall under the pointer is built on, the lowest,
 * which is refused. Over no wall, the storey above the player's cell under the pointer, so the cursor still shows the
 * mode and the server refuses it. `storeys` are consecutive from the player's own upward; `planeOffset(level)` is how
 * far a storey is drawn from the player's. Automatic selection (`requireWall`) fills a gap between
 * walls before stacking on an overlapping cap, and otherwise disables the empty-cell fallback.
 */
export const stackWallPick = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number, requireWall = false,
    plannedWallAt?: (point: Coordinate, level: number) => boolean): StackPick | undefined => {
    let builtOn: StackPick | undefined
    for (let index = 0; index < storeys.length; index++) {
        const { level, map } = storeys[index]
        const { at, fy } = pointOnPlane(world, planeOffset(level))
        // A wall's cap can occupy an empty structural cell. Close a one-cell gap on the
        // lowest level before stacking on the wall whose artwork extends into that gap.
        const hasWall = (point: Coordinate) => wallCellAt(map, point) || !!plannedWallAt?.(point, level)
        const gap = !hasWall(at) && (
            hasWall({ x: at.x, y: at.y - 1 }) && hasWall({ x: at.x, y: at.y + 1 }) ||
            hasWall({ x: at.x - 1, y: at.y }) && hasWall({ x: at.x + 1, y: at.y }))
        if (requireWall && gap) {
            // Use the server's wall-shape rule: a room's center may look like a gap, but
            // filling it can create unrenderable solid wall blocks around the cap.
            const local = Array.from({ length: 5 }, (_, y) => Array.from({ length: 5 }, (_, x) =>
                x === 2 && y === 2 || hasWall({ x: at.x + x - 2, y: at.y + y - 2 })))
            const indices = autotile(local)
            const computable = [1, 2, 3].every(y => [1, 2, 3].every(x => wallAutoTileCellComputable(indices[y][x])))
            if (computable) return { target: withLevel(at, level === storeys[0].level ? undefined : level), at }
        }
        const above = storeys.find(storey => storey.level === level + 1)
        for (const base of wallsDrawnAt(map, at, fy)) {
            const pick = { target: withLevel(base, level + 1), at }
            if (!(above && wallCellAt(above.map, base)) && !plannedWallAt?.(base, level + 1)) return pick
            builtOn = builtOn ?? pick
        }
    }
    if (builtOn) return builtOn
    if (requireWall) return undefined
    const at = cellOnPlane(world, planeOffset(storeys[0].level))
    return { target: withLevel(at, storeys[0].level + 1), at }
}

/** During a wall-stacking stroke: the wall under the pointer on the storey below, or the bare cell. */
export const stackStrokePick = (world: Coordinate, level: number, storeys: StoreyView[],
    planeOffset: (level: number) => number, plannedWallAt?: (point: Coordinate, level: number) => boolean): StackPick => {
    const { at, fy } = pointOnPlane(world, planeOffset(level - 1))
    const below = storeys.find(storey => storey.level === level - 1)
    const destination = storeys.find(storey => storey.level === level)
    const candidates = below ? wallsDrawnAt(below.map, at, fy) : []
    const unfinished = candidates.find(point => !(destination && wallCellAt(destination.map, point)) && !plannedWallAt?.(point, level))
    return { target: withLevel(unfinished || candidates[0] || at, level), at }
}

/**
 * A roof for a storey above the player's: on each raised storey's roof plane (its plane, lifted by `roofOffset`), from
 * the top down, the first cell over a wall or roof of that storey, so pointing at the top of a raised wall roofs that
 * storey. Undefined: the roof is for the player's own storey.
 */
export const atopRoofTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number,
    roofOffset: number): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= 1; index--) {
        const { level, map } = storeys[index]
        const cell = cellOnPlane(world, planeOffset(level) + roofOffset)
        if (wallCellAt(map, cell) || roofCellAt(map, cell)) return withLevel(cell, level)
    }
    return undefined
}

/** A storey's own floor on `cell`: not an opening, and not a wall top's floor. */
const floorSurfaceAt = (map: WallMap, cell: Coordinate): boolean => {
    if (map.isFloorOpeningAt?.(cell.x, cell.y)) return false
    const delta = map.getMapDeltaAt?.(cell.x, cell.y, "map2")
    // A wall's top is floored for walking on, not built on: to a builder the cell is bare.
    if (delta?.removed || delta?.context?.previewTarget || isWallTopFloor(delta?.context?.itemType)) return false
    const tile = map.resolveTileMapFromLayerId?.("map2")?.[cell.x]?.[cell.y]
    return !!delta || !!tile && tile !== "0"
}

/** The highest existing floor under the pointer, picked on its rendered plane. */
export const floorSurfaceTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= 1; index--) {
        const { level, map } = storeys[index]
        const cell = cellOnPlane(world, planeOffset(level))
        if (floorSurfaceAt(map, cell)) return withLevel(cell, level)
    }
    return undefined
}

/** What a ladder reads of a storey: its built pieces, and its walls' tiles where the map can say what they are. */
export type LadderMap = WallMap & Partial<Pick<TileMapMetaProvider,
    "resolveTileMeta" | "width" | "height" | "isCollisionAt" | "isStairAt" | "getUpperFloorProvider">>

/** A storey a ladder can be aimed at, as it is drawn for the player. */
export interface LadderStorey {
    level: number
    map: LadderMap
    /** False while the storey's roofs are hidden. */
    roofVisible?: boolean
    /** Its cells ("x,y") cut away over the player indoors, where nothing of it is drawn. */
    cut?: ReadonlySet<string>
}

/** A wall's top on `cell` a jump could land on (wall_top.ts); none on a map that can't say what its tiles are. */
const climbableTopAt = (map: LadderMap, above: LadderMap | undefined, cell: Coordinate): boolean =>
    typeof map.resolveTileMeta === "function" && wallClimbableAt(map as TileMapMetaProvider, cell) &&
    wallTopOpenAt(map as TileMapMetaProvider, above as TileMapMetaProvider | undefined, cell)

/**
 * The highest surface of `storeys` at the cells `cellOn(level, lift)` picks, from the top storey down: a wall's top a jump
 * could land on (it stands a storey up), a roof (a storey up), then a floor; under them all, the ground, or the lowest
 * storey drawn when the ground isn't. The roof over the player's own storey `own` is none: a ladder goes up through it.
 */
const ladderSurface = (storeys: LadderStorey[], own: number, roofOffset: number,
    cellOn: (level: number, lift?: number) => Coordinate): BuildTarget => {
    const target = (cell: Coordinate, level: number) => withLevel(cell, level === own ? undefined : level)
    const drawn = (storey: LadderStorey, cell: Coordinate) => !storey.cut?.has(`${cell.x},${cell.y}`)
    const highestFirst = storeys.slice().sort((a, b) => b.level - a.level)
    for (const storey of highestFirst) {
        const { level, map } = storey
        const above = storeys.find(other => other.level === level + 1)?.map ?? map.getUpperFloorProvider?.()
        const top = cellOn(level + 1)
        if (drawn(storey, top) && climbableTopAt(map, above, top)) return target(top, level + 1)
        const roof = cellOn(level, roofOffset)
        if (level !== own && storey.roofVisible !== false && drawn(storey, roof) && roofCellAt(map, roof)) return target(roof, level + 1)
        const floor = cellOn(level)
        if (level === 0 || drawn(storey, floor) && floorSurfaceAt(map, floor)) return target(floor, level)
    }
    const lowest = highestFirst[highestFirst.length - 1]?.level ?? 0
    return target(cellOn(lowest), lowest)
}

/**
 * Where a ladder aimed at `world` stands: on the highest surface drawn under the pointer (ladderSurface), each looked for
 * on the plane it is drawn on, `planeOffset(level)` from the player's, a roof lifted by `roofOffset` more. `storeys` are
 * the ones drawn for the player, their own `own` among them.
 */
export const ladderSurfaceTarget = (world: Coordinate, storeys: LadderStorey[], own: number,
    planeOffset: (level: number) => number, roofOffset: number): BuildTarget =>
    ladderSurface(storeys, own, roofOffset, (level, lift = 0) => cellOnPlane(world, planeOffset(level) + lift))

/** Where a ladder placed in the cell the player faces stands: the highest surface over it, from their own storey down. */
export const ladderColumnTarget = (cell: Coordinate, storeys: LadderStorey[], own: number): BuildTarget =>
    ladderSurface(storeys.filter(storey => storey.level <= own), own, 0, () => ({ x: cell.x, y: cell.y }))

/** Shift floors build above the highest floor or wall under the pointer, even when the player stays below. */
export const stackFloorTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number): BuildTarget => {
    let sourceLevel = floorSurfaceTarget(world, storeys, planeOffset)?.level ?? storeys[0].level
    for (let index = storeys.length - 1; index >= 0; index--) {
        const { level, map } = storeys[index]
        if (level <= sourceLevel) break
        const { at, fy } = pointOnPlane(world, planeOffset(level))
        if (wallsDrawnAt(map, at, fy).length) {
            sourceLevel = level
            break
        }
    }
    const level = sourceLevel + 1
    return withLevel(cellOnPlane(world, planeOffset(level)), level)
}

/** A floor or wall aimed at a visible roof belongs to the storey immediately above that roof. */
export const atopFloorTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number,
    roofOffset: number, includeOwnRoof = true): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= (includeOwnRoof ? 0 : 1); index--) {
        const { level, map } = storeys[index]
        const cell = cellOnPlane(world, planeOffset(level) + roofOffset)
        if (roofCellAt(map, cell)) return withLevel(cell, level + 1)
    }
    return undefined
}

/** Shared wall artwork can cover several cells: prefer the first wall without a floor above it. */
export const wallFloorTarget = (world: Coordinate, storeys: StoreyView[], planeOffset: (level: number) => number,
    plannedFloorAt?: (point: Coordinate, level: number) => boolean): BuildTarget | undefined => {
    for (let index = storeys.length - 1; index >= 0; index--) {
        const { level, map } = storeys[index]
        const { at, fy } = pointOnPlane(world, planeOffset(level))
        const upper = storeys.find(storey => storey.level === level + 1)
        for (const wall of wallsDrawnAt(map, at, fy)) {
            if (upper && floorCellAt(upper.map, wall, true) || plannedFloorAt?.(wall, level + 1)) continue
            return withLevel(wall, level + 1)
        }
    }
    return undefined
}

const floorCellAt = (map: WallMap, at: Coordinate, nativeFloor: boolean): boolean => {
    if (map.isFloorOpeningAt?.(at.x, at.y)) return false
    const delta = map.getMapDeltaAt?.(at.x, at.y, "map2")
    if (delta?.removed || delta?.context?.previewTarget) return false
    if (delta) return ItemTypeCharacteristics[delta.context?.itemType]?.buildType === "floor" && !isWallTopFloor(delta.context?.itemType)
    const tile = nativeFloor && map.resolveTileMapFromLayerId?.("map2")?.[at.x]?.[at.y]
    return !!tile && tile !== "0"
}

/** Extend an existing ceiling surface across an edge, choosing the highest visible neighbour.
 * A roof on storey L and a floor on L+1 are the two sides of that same ceiling.
 * Ground floors only attract floors; they cannot support a roof beneath the ground.
 */
export const adjacentSurfaceTarget = (world: Coordinate, kind: "floor" | "roof", storeys: StoreyView[],
    planeOffset: (level: number) => number, roofOffset: number, includeOwnRoof = true): BuildTarget | undefined => {
    const own = storeys[0].level
    const adjacent = (at: Coordinate, has: (point: Coordinate) => boolean) =>
        [{ x: at.x - 1, y: at.y }, { x: at.x + 1, y: at.y }, { x: at.x, y: at.y - 1 }, { x: at.x, y: at.y + 1 }].some(has)
    const target = (at: Coordinate, level: number) => withLevel(at, level === own ? undefined : level)
    for (let index = storeys.length - 1; index >= 0; index--) {
        const { level, map } = storeys[index]
        if (includeOwnRoof || index > 0) {
            const cell = cellOnPlane(world, planeOffset(level) + roofOffset)
            if (adjacent(cell, point => roofCellAt(map, point))) return target(cell, kind === "floor" ? level + 1 : level)
        }
        const destination = kind === "floor" ? level : level - 1
        if (destination < own) continue
        // Pick on the destination plane, so the printed tile stays under the pointer.
        const cell = cellOnPlane(world, planeOffset(destination) + (kind === "roof" ? roofOffset : 0))
        if (adjacent(cell, point => floorCellAt(map, point, index > 0 || own > 0))) return target(cell, destination)
    }
    return undefined
}

/** During a stroke that paints storey `level`: the cell under the pointer on that storey's plane. */
export const storeyStrokeTarget = (world: Coordinate, level: number, planeOffset: (level: number) => number, lift = 0): BuildTarget =>
    withLevel(cellOnPlane(world, planeOffset(level) + lift), level)

/** Another storey as it is drawn right now: `yOffset` from the player's own (up is negative). */
export interface DrawnStorey extends StoreyView {
    roomId: string
    yOffset: number
    roofVisible?: boolean
    /** Its cells ("x,y") cut away over the player indoors. */
    cut?: ReadonlySet<string>
}

/** Visible storeys answer from highest to lowest; each tile is picked on its rendered layer plane. */
export const storeyTileTarget = (world: Coordinate, level: number, storeys: DrawnStorey[],
    own: () => TileInteractionTarget | undefined): TileInteractionTarget | undefined => {
    const tileOn = (storey: DrawnStorey): TileInteractionTarget | undefined => {
        const layers = [...(storey.roofVisible === false ? [] : ["sky"]), "sprite3", "sprite2", "sprite", "map3", "map2"]
        for (const layerId of layers) {
            const yOffset = storey.yOffset + (layerId === "sky" ? houseRoofOffset() : 0)
            const target = tileInteractionsAt(storey.map, cellOnPlane(world, yOffset), { includeRoof: false, layerIds: [layerId] })
            const interactions = target?.interactions.filter(verb => verb.name === "dismantle")
            if (interactions?.length) return { ...target, interactions, roomId: storey.roomId, yOffset }
        }
        return undefined
    }
    const highestFirst = storeys.slice().sort((a, b) => b.level - a.level)
    for (const storey of highestFirst) {
        const target = storey.level > level && tileOn(storey)
        if (target) return target
    }
    const target = own()
    if (target) return target
    for (const storey of highestFirst) {
        const target = storey.level < level && tileOn(storey)
        if (target) return target
    }
    return undefined
}

/**
 * Which hatch a ladder extension was aimed by: a flight's top hatch on its own storey, the hatch a
 * roof cutaway draws at the top of an interior flight, or the first ladder's taller original hatch.
 */
export type LadderHatch = "top" | "ceiling" | "original"

/** Only the hatch of the topmost flight extends a ladder; its body remains selectable. */
export const ladderExtensionTarget = (world: Coordinate, views: StoreyView[], yOffset: (level: number) => number,
    options?: { visibleLevels?: ReadonlySet<number>; ceilingHeight?: (view: StoreyView, at: Coordinate) => number | undefined;
        onPick?: (target: BuildTarget, cursor: Coordinate, hatch: LadderHatch) => void }): BuildTarget | undefined => {
    const pick = (at: Coordinate, level: number, hatchCenterY: number, hatch: LadderHatch): BuildTarget => {
        const target = { ...at, level }
        options?.onPick?.(target, { x: at.x * TileSize, y: hatchCenterY - TileSize / 2 }, hatch)
        return target
    }
    const contextAt = (view: StoreyView, at: Coordinate) => {
        const delta = view.map.getMapDeltaAt(at.x, at.y, "sprite2")
        return delta && !delta.removed ? delta.context : undefined
    }
    for (const view of [...views].sort((a, b) => b.level - a.level)) {
        if (options?.visibleLevels && !options.visibleLevels.has(view.level)) continue
        const column = Math.floor(world.x / TileSize), row = Math.floor((world.y - yOffset(view.level)) / TileSize)
        for (let x = column - 1; x <= column + 1; x++) for (let y = row; y <= row + 3; y++) {
            const at = { x, y }, context = contextAt(view, at)
            if (!context || Math.abs(world.x - (x + .5) * TileSize) > 22) continue
            const links = context.context?.stairLandings ?? context.stairLandings
            const baseline = (y + 1) * TileSize + yOffset(view.level)
            if (context.itemType === "stairs-down" && !links?.up && Math.abs(world.y - (baseline - 8)) <= 12)
                return pick(at, view.level, baseline - 8, "top")
            // The cutaway draws this flight's hatch at its full interior height, while
            // the logical endpoint is on an upper storey that is intentionally hidden.
            const ceilingHeight = context.itemType === "stairs-up" && links?.up && options?.ceilingHeight?.(view, at)
            if (ceilingHeight && Math.abs(world.y - (baseline - ceilingHeight + 7)) <= 12) {
                const above = views.find(other => other.level === view.level + 1)
                if (!above || contextAt(above, at)?.itemType === "stairs-down") return pick(at, view.level + 1, baseline - ceilingHeight + 7, "ceiling")
            }
            // The first freestanding ladder has a taller original hatch. Aim there too.
            if (context.itemType === "stairs-up" && !links && Math.abs(world.y - (baseline - 80)) <= 10) {
                const above = views.find(other => other.level === view.level + 1)
                const upper = above && contextAt(above, at)
                if (upper?.itemType === "stairs-down" || !upper && (context.context?.stairLanding ?? context.stairLanding)?.roomId)
                    return pick(at, view.level + 1, baseline - 80, "original")
            }
        }
    }
}
